<template>
  <table class="min-w-full">
    <thead class="bg-gray-50">
      <tr class="first-child:pl-3 last-child:pr-3 child:py-2 sticky top-0 text-xs child:border-b child:border-gray-200 font-semibold text-gray-600 z-10">
        <th class="text-left uppercase tracking-wider bg-gray-50 w-2/12">
          {{ $t('HeadContract.ProgressClaimsShow.table.contractWorks') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          {{ $t('HeadContract.ProgressClaimsShow.table.budget') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.ProgressClaimsShow.table.currentClaim') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.ProgressClaimsShow.table.currentCertified') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.ProgressClaimsShow.table.previous') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50 pr-3">
          {{ $t('HeadContract.ProgressClaimsShow.table.cumulative') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50">
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showPercentages ? 'Hide Percentages' : 'Show Percentages'"
              @click.stop="showPercentages = !showPercentages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showPercentages}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showPercentages">{{ $t('HeadContract.ProgressClaimsShow.table.currentClaimPercentage') }}</span>
          </div>
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          {{ $t('HeadContract.ProgressClaimsShow.table.currentCertifiedPercentage') }}
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          {{ $t('HeadContract.ProgressClaimsShow.table.previousPercentage') }}
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50 pr-3"
        >
          {{ $t('HeadContract.ProgressClaimsShow.table.cumulativePercentage') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50">
          {{ $t('HeadContract.ProgressClaimsShow.table.variance') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          {{ $t('HeadContract.ProgressClaimsShow.table.costToComplete') }}
        </th>
      </tr>
    </thead>

    <tbody
      v-if="claim"
      class="bg-white divide-y divide-gray-200"
    >
      <TradeRow
        v-for="trade in claim.trades"
        :key="trade.id"
        :trade="trade"
        :claim="claim"
        :show-percentages="showPercentages"
      />

      <!-- Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          {{ $t('HeadContract.ProgressClaimsShow.table.subtotal') }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span
              v-if="totalCosts.trades.budget !== 0"
              class="text-gray-600"
            >Works:</span> {{ $tcc(totalCosts.trades.budget) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span
              v-if="totalCosts.trades.budget !== 0"
              class="text-gray-600"
            >Adjustments:</span> {{ $tcc(-(totalCosts.trades.budget - totalCosts.trades.adjustedBudget)) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span class="text-gray-600">Sum:</span> {{ $tcc(totalCosts.trades.adjustedBudget) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget === 0">
            {{ $tcc(totalCosts.trades.budget) }}
          </div>
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_certified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ isNumber(totalCosts.trades.current_claim / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_claim / totalCosts.trades.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.current_certified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_certified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.previous / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.previous / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ isNumber(totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(Math.abs(totalCosts.trades.current_claim - totalCosts.trades.current_certified)) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(Math.abs(totalCosts.trades.budget - totalCosts.trades.current_certified)) }}
        </td>
      </tr>

      <!-- Variation Orders Section -->
      <template
        v-for="(costs, key) in variationOrderCosts"
        :key="key"
      >
        <VariationOrderRow
          :claim="claim"
          :costs="costs"
          :title="key"
          :variation-order-type="VariationOrderType[key]"
          :show-percentages="showPercentages"
        />
      </template>

      <!-- Variation Orders Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          {{ $t('HeadContract.ProgressClaimsShow.table.subtotal') }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ $tcc(totalCosts.variationOrders.budget) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.variationOrders.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.variationOrders.current_certified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(totalCosts.variationOrders.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ $tcc(totalCosts.variationOrders.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.current_claim / totalCosts.variationOrders.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.current_certified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.previous / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget > 0 ? $tn((totalCosts.variationOrders.cumulativeCertified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ $tcc(Math.abs(totalCosts.variationOrders.current_claim - totalCosts.variationOrders.current_certified)) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ $tcc(Math.abs(totalCosts.variationOrders.budget - totalCosts.variationOrders.current_certified)) }}
        </td>
      </tr>

      <SharedFundRow
        v-if="sharedFunds.length && claim"
        :shared-funds="sharedFunds"
        :claim="claim"
        :show-percentages="showPercentages"
      />

      <!-- Grand Total Row -->
      <tr class="child:py-4 first-child:pl-3 last-child:pr-3 bg-blue-50 child:border-t-2 child:border-blue-200 sticky bottom-0">
        <td class="font-bold text-blue-900">
          {{ $t('HeadContract.ProgressClaimsShow.table.grandTotal') }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ $tcc(grandTotal.budget) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ $tcc(grandTotal.current_claim) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ $tcc(grandTotal.current_certified) }}

          <p class="text-gray-500">
            Payable: {{ $tcc(claim.totalPayable) }}
          </p>
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ $tcc(grandTotal.previous) }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ $tcc(grandTotal.cumulativeCertified) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget > 0 ? $tn((grandTotal.current_claim / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget)) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget > 0 ? $tn((grandTotal.current_certified / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget > 0 ? $tn((grandTotal.previous / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget)) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget > 0 ? $tn((grandTotal.cumulativeCertified / (totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ $tcc(Math.abs(grandTotal.current_claim - grandTotal.current_certified)) }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ $tcc(Math.abs((totalCosts.variationOrders.budget + totalCosts.trades.budget + totalCosts.sharedFunds.budget) - (totalCosts.variationOrders.current_certified + totalCosts.trades.current_certified + totalCosts.sharedFunds.current_certified))) }}
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import SharedFundRow from '@/components/ProgressClaims/SharedFundRow.vue';
import TradeRow from '@/components/ProgressClaims/TradeRow.vue';
import VariationOrderRow from '@/components/ProgressClaims/VariationOrderRow.vue';
import { australianCurrency, australianNumber, isNumber } from '@/helpers';
import SharedFund from '@/models/SharedFund';
import { VariationOrderStatus, VariationOrderType } from '@/models/VariationOrder';
import VariationOrderCost from '@/models/VariationOrderCost';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { useVariationOrderCostsStore } from '@/store/variationOrderCosts';
import { Collection } from 'pinia-orm';
import { computed, ref } from 'vue';

const props = defineProps<{
  claimId: string;
  projectId: string;
}>();

const claimsStore = useClaimsStore();
const projectsStore = useProjectsStore();
const sharedFundsStore = useSharedFundsStore();
const showPercentages = ref(false);

const claim = computed(() => {
  // TODO: 10/11/23 constrain variation orders to only those with cost for the current claim
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items', (query) => {
          query.whereNull('parent_id');
        })
          .where('boq_type', 'direct')
          .orderBy('sort_order');
      })
      .with('trade_item_costs')
      .with('variation_orders', (query) => {
        query.whereIn('status', [VariationOrderStatus.approved, VariationOrderStatus.principled])
          .with('trade_items');
      })
      .with('variation_order_costs')
      .with('shared_fund_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const sharedFunds = computed(() => {
  if(!projectsStore.project?.project_group_id) {
    return [] as SharedFund[];
  }

  return sharedFundsStore.models
    .with('costs')
    .with('project_group', (query) => query.with('projects'))
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      adjustedBudget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
    },

    variationOrders: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
    },

    sharedFunds: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
    },
  };

  if(claim.value) {
    claim.value.trades?.forEach((trade) => {
      trade.trade_items.forEach((tradeItem) => {
        totals.trades.budget += tradeItem.childBudget();
        totals.trades.adjustedBudget += tradeItem.childAdjustedBudget();
        totals.trades.previous += tradeItem.childPrevious(claim.value);
      });
    });

    claim.value.trade_item_costs.forEach((cost) => {
      totals.trades.current_claim += cost.current_claim;
      totals.trades.current_certified += cost.current_certified;
    });

    claim.value.variation_orders?.forEach((variationOrder) => {
      totals.variationOrders.budget += variationOrder.budget;
    });

    claim.value.variation_order_costs?.forEach((variationOrderCost) => {
      totals.variationOrders.current_claim += variationOrderCost.current_claim;
      totals.variationOrders.current_certified += variationOrderCost.current_certified;
    });

    claim.value.shared_fund_costs?.forEach((sharedFundCost) => {
      totals.sharedFunds.current_claim += sharedFundCost.current_claim || 0;
      totals.sharedFunds.current_certified += sharedFundCost.current_certified || 0;
    });

    if(sharedFunds.value.length > 0) {
      sharedFunds.value.forEach((fund) => {
        totals.sharedFunds.budget += fund.budget;
        totals.sharedFunds.previous += fund.previous(claim.value);
      });
    }
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;

  totals.variationOrders.cumulativeCertified = totals.variationOrders.current_certified +
    totals.variationOrders.previous;

  totals.sharedFunds.cumulativeCertified = totals.sharedFunds.current_certified + totals.sharedFunds.previous;

  return totals;
});

const grandTotal = computed(() => {
  return {
    budget: totalCosts.value.variationOrders.budget + totalCosts.value.trades.adjustedBudget +
      totalCosts.value.sharedFunds.budget,

    current_claim: totalCosts.value.variationOrders.current_claim + totalCosts.value.trades.current_claim +
      totalCosts.value.sharedFunds.current_claim,

    current_certified: totalCosts.value.variationOrders.current_certified +
      totalCosts.value.trades.current_certified + totalCosts.value.sharedFunds.current_certified,

    previous: totalCosts.value.variationOrders.previous + totalCosts.value.trades.previous +
      totalCosts.value.sharedFunds.previous,

    cumulativeCertified: totalCosts.value.variationOrders.cumulativeCertified +
      totalCosts.value.trades.cumulativeCertified + totalCosts.value.sharedFunds.cumulativeCertified,
  };
});

const variationOrderCostsStore = useVariationOrderCostsStore();

const variationOrderCosts = computed(() => {
  const costs: Partial<{ [k in keyof typeof VariationOrderType]: Collection<VariationOrderCost> }> = {};

  for(const key in VariationOrderType) {
    costs[key] = variationOrderCostsStore.models.with('variation_order').where('claim_id', claim.value?.id).whereHas(
      'variation_order',
      (query) => {
        query.where('type', VariationOrderType[key]);
      },
    ).get()
      .sort((a, b) => {
        const aRef = a.variation_order?.client_ref || a.variation_order?.code || '';
        const bRef = b.variation_order?.client_ref || b.variation_order?.code || '';

        return aRef.localeCompare(bRef);
      });
  }

  return costs;
});
</script>

<style scoped></style>
